// src/components/ContactFormModal.js
import React, { useContext, useState, useEffect } from "react";
import { LangContext } from "../lang/LangProvider";
import { useAnalytics } from "../hooks/useAnalytics";
import "./ContactFormModal.css";

const ContactFormModal = ({ isOpen, onClose, context = "about" }) => {
  const { lang } = useContext(LangContext);
  const { trackModalOpen, trackFormSubmit, trackFormValidationError } = useAnalytics();
  const [form, setForm] = useState({
    name: "",
    email: "",
    reason: "",
    message: "",
    gdpr: false,
  });
  const [formError, setFormError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Revisione 29/09/2026: rimosso il motivo "Consulenza finanziaria" — il servizio non è
  // avviato e il sito non consente il conferimento di incarichi.
  const reasons = {
    it: [
      { value: "", label: "Seleziona un motivo..." },
      { value: "informazioni", label: "Informazioni generali" },
      { value: "contenuti", label: "Articoli e serie" },
      { value: "tools", label: "Tool e simulatori" },
      { value: "partnership", label: "Partnership / collaborazione" },
      { value: "altro", label: "Altro" },
    ],
    en: [
      { value: "", label: "Select a reason..." },
      { value: "informazioni", label: "General information" },
      { value: "contenuti", label: "Articles & series" },
      { value: "tools", label: "Tools & simulators" },
      { value: "partnership", label: "Partnership / collaboration" },
      { value: "altro", label: "Other" },
    ],
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.reason || !form.message) {
      setFormError(
        lang === "en"
          ? "Please fill in all required fields."
          : "Compila tutti i campi obbligatori."
      );
      trackFormValidationError("contact_form", "missing_fields");
      return;
    }
    if (!form.gdpr) {
      setFormError(
        lang === "en"
          ? "Please confirm you have read the privacy notice to continue."
          : "Conferma di aver letto l'informativa privacy per continuare."
      );
      trackFormValidationError("contact_form", "missing_gdpr");
      return;
    }
    setFormError("");
    const subject =
      lang === "en"
        ? `AlphaFrame contact: ${form.reason}`
        : `Contatto AlphaFrame: ${form.reason}`;
    const body =
      lang === "en"
        ? `Name: ${form.name}\nEmail: ${form.email}\nReason: ${form.reason}\n\nMessage:\n${form.message}\n\n[Privacy notice read]`
        : `Nome: ${form.name}\nEmail: ${form.email}\nMotivo: ${form.reason}\n\nMessaggio:\n${form.message}\n\n[Presa visione informativa privacy]`;
    trackFormSubmit("contact_form", true, form.reason);
    window.location.href = `mailto:alphaframeanalytics.it@gmail.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Traccia apertura modale
  useEffect(() => {
    if (isOpen) {
      trackModalOpen("contact_form", context, lang);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const resetForm = () => {
    setForm({ name: "", email: "", reason: "", message: "", gdpr: false });
    setFormError("");
    setSubmitted(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="cf-overlay" onClick={handleClose}>
      <div className="cf-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="cf-close"
          onClick={handleClose}
          aria-label={lang === "en" ? "Close" : "Chiudi"}
        >
          ✕
        </button>

        {submitted ? (
          <div className="cf-success">
            <h2>
              {lang === "en"
                ? "Thank You!"
                : "Grazie!"}
            </h2>
            <p>
              {lang === "en"
                ? "Your email client has been opened with your message. Please send it to complete your request."
                : "Il tuo client email è stato aperto con il messaggio. Invialo per completare la richiesta."}
            </p>
            <button
              className="cf-btn-primary"
              onClick={handleClose}
            >
              {lang === "en" ? "Close" : "Chiudi"}
            </button>
          </div>
        ) : (
          <>
            <h2 className="cf-title">
              {lang === "en"
                ? "Contact Us"
                : "Contattaci"}
            </h2>
            <p className="cf-subtitle">
              {lang === "en"
                ? "Questions, collaborations or information requests? Write to us. This form is for general information only: it cannot be used to confer investment advisory engagements."
                : "Domande, collaborazioni o richieste di informazioni? Scrivici. Il modulo è per informazioni generali: non consente il conferimento di incarichi di consulenza."}
            </p>

            <form className="cf-form" onSubmit={handleFormSubmit} noValidate>
              <div className="cf-form-row">
                <label htmlFor="cf-name">
                  {lang === "en" ? "Name *" : "Nome *"}
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleFormChange}
                    placeholder={lang === "en" ? "Your name" : "Il tuo nome"}
                  />
                </label>
                <label htmlFor="cf-email">
                  {lang === "en" ? "Email *" : "Email *"}
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleFormChange}
                    placeholder="email@example.com"
                  />
                </label>
              </div>

              <label htmlFor="cf-reason">
                {lang === "en" ? "Reason *" : "Motivo *"}
                <select
                  id="cf-reason"
                  name="reason"
                  required
                  value={form.reason}
                  onChange={handleFormChange}
                >
                  {(reasons[lang] || reasons.it).map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>

              <label htmlFor="cf-message">
                {lang === "en" ? "Message *" : "Messaggio *"}
                <textarea
                  id="cf-message"
                  name="message"
                  rows={4}
                  required
                  value={form.message}
                  onChange={handleFormChange}
                  placeholder={
                    lang === "en"
                      ? "Describe your request..."
                      : "Descrivi la tua richiesta..."
                  }
                />
              </label>

              <label className="cf-gdpr-label">
                <input
                  type="checkbox"
                  name="gdpr"
                  checked={form.gdpr}
                  onChange={handleFormChange}
                />
                <span>
                  {lang === "en" ? (
                    <>
                      I have read the{" "}
                      <a
                        href={`/${lang}/privacy`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        privacy notice
                      </a>{" "}
                      (art. 13 GDPR). My data will be used only to reply to this message. *
                    </>
                  ) : (
                    <>
                      Ho letto l'
                      <a
                        href={`/${lang}/privacy`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        informativa privacy
                      </a>{" "}
                      (art. 13 GDPR). I dati saranno usati solo per rispondere a questo messaggio. *
                    </>
                  )}
                </span>
              </label>

              {formError && <p className="cf-error">{formError}</p>}

              <button type="submit" className="cf-btn-primary">
                {lang === "en" ? "Send message" : "Invia messaggio"}
              </button>

              <p className="cf-note">
                <small>
                  {lang === "en"
                    ? "* Required fields. Your email client will open to send the message. No data is stored on our servers."
                    : "* Campi obbligatori. Si aprirà il tuo client email per inviare il messaggio. Nessun dato viene memorizzato sui nostri server."}
                </small>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ContactFormModal;
